import { X } from "lucide-react";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";

export type DeviceType = "microphone" | "speaker" | "camera";

export type Props = {
  deviceType: DeviceType;
  deviceLabel: string;
  /** When provided, renders an Undo button next to the message (connect path). */
  onUndo?: () => void | Promise<void>;
  onDismiss: () => void;
};

/**
 * Toast surface for device hot-plug auto-switches (mic, speaker, camera).
 * Pure presentational — receives the device type + label and an optional
 * `onUndo` for the connect path. Mirrors the visual chrome applied to default
 * sonner toasts by `themes/pollinator/ui/Toast` (bg-brand-50 /
 * border-brand-800 / shadow-lg) so a custom toast (`toast.custom`) matches the
 * rest of the in-meeting toast stack.
 */
export function DeviceHotPlugToast({
  deviceType,
  deviceLabel,
  onUndo,
  onDismiss,
}: Props) {
  return (
    <div
      role="alert"
      aria-live="polite"
      className="bg-brand-50 text-brand-950 border border-brand-800 shadow-lg rounded-md px-4 py-3 flex items-start gap-3 max-w-md"
    >
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <Type variant="body2">
          Switched {deviceType} to{" "}
          <span className="font-medium">{deviceLabel}</span>
        </Type>
        {onUndo && (
          <div>
            <Button variant="secondary" onClick={onUndo}>
              Undo
            </Button>
          </div>
        )}
      </div>
      <Button
        variant="ghost"
        iconComponent={X}
        onClick={onDismiss}
        aria-label="Dismiss"
      />
    </div>
  );
}
